import { Controller } from "@hotwired/stimulus";
import { nearFrameEdge, workspace, followFrame, pageName, themeColor } from "../lib/editor_stage";
import {
  canvasImageSource,
  FabricEditor,
  DropHandler,
  rulesOf,
  type ImageFrame,
  type LayerData,
  type FontsConfig,
  type AlignEdge,
  type Arrangement,
  type DistributeAxis,
  type LockMode,
} from "@frabr/fabric-editor";
import type { FabricObject } from "fabric";
import { FabricControls } from "../lib/fabric_controls";
import { FabricLayout } from "../lib/fabric_layout";
import { FabricAppearance } from "../lib/fabric_appearance";
import { UndoManager } from "../lib/undo_manager";
import { uploadToLibrary } from "../lib/media_library";
import { MediaPicker } from "../lib/media_picker";
import { ToolboxDropZone } from "../lib/toolbox_drop_zone";
import {
  armToolboxDrag, canvasPlacement, placeToolboxItem, dropToolboxItem, type ToolboxHost,
} from "../lib/toolbox_dispatch";
import { mediaKind, publishPlacements, type Placement } from "../lib/toolbox_placement";
import { imageMetaOf, isBlankLayers, mediumMeta, type ImageMeta } from "../lib/composition_document";
import {
  BG_LAYER_ID,
  backgroundStateOf,
  colorBackgroundLayer,
  imageBackgroundLayer,
  refitBackgroundLayer,
} from "../lib/fabric_background";
import {
  attachBoundTextEditing,
  bindSelectedField,
  unbindSelectedField,
  setSelectedText,
  insertTokenAtCaret,
  interpolateBoundFields,
  revealHiddenLayer,
  withExpressions,
} from "../lib/fabric_bindings";
import { HiddenLayerBadges } from "../lib/hidden_layer_badges";
import { hasResolvableLegacyClip, streamShapeCatalogs } from "../lib/shape_catalogs";

/**
 * Contrôleur Stimulus pour l'éditeur d'images Fabric.js
 *
 * Orchestre l'interaction entre le DOM et le FabricEditor.
 * La logique métier est déléguée à FabricEditor et ses managers.
 */
export default class extends Controller<HTMLElement> {
  // Targets (layer control elements are now found via data-role by FabricControls)
  static targets = [
    "canvas",
    "container",
    "previewArea",
    // Plan de travail : la couche calée sous le cadre (damier, fonds HTML), et le repère
    // du drop en bord, au-dessus du canvas
    "frame",
    "frameCue",
    // La page dans le cadre : son nom, l'invitation à poser un fond (fabric/_page_overlay)
    "pageOverlay",
    "pageName",
    "pageEmpty",
    "legacyHint",
    "layerControls",
    "replaceOverlay",
    "undoButton",
    "redoButton",
    // Les calques masqués par leur condition : la couche des pastilles, leurs gabarits, le
    // panneau (lib/hidden_layer_badges)
    "hiddenLayers",
    "hiddenBadge",
    "hiddenVariable",
    "hiddenPanel",
  ];


  declare canvasTarget: HTMLCanvasElement;
  declare containerTarget: HTMLElement;
  declare previewAreaTarget: HTMLElement;
  declare frameTarget: HTMLElement;
  declare hasFrameTarget: boolean;
  declare frameCueTarget: HTMLElement;
  declare hasFrameCueTarget: boolean;
  declare pageOverlayTarget: HTMLElement;
  declare hasPageOverlayTarget: boolean;
  declare pageNameTarget: HTMLElement;
  declare hasPageNameTarget: boolean;
  declare pageEmptyTarget: HTMLElement;
  declare hasLegacyHintTarget: boolean;
  declare legacyHintTarget: HTMLElement;
  declare hasPageEmptyTarget: boolean;
  declare layerControlsTarget: HTMLElement;
  declare replaceOverlayTarget: HTMLElement;
  declare undoButtonTarget: HTMLButtonElement;
  declare redoButtonTarget: HTMLButtonElement;

  declare hasReplaceOverlayTarget: boolean;
  declare hiddenLayersTarget: HTMLElement;
  declare hasHiddenLayersTarget: boolean;
  declare hiddenBadgeTarget: HTMLTemplateElement;
  declare hiddenVariableTarget: HTMLTemplateElement;
  declare hiddenPanelTarget: HTMLElement & { showPopover(): void; hidePopover(): void };
  declare hasUndoButtonTarget: boolean;
  declare hasRedoButtonTarget: boolean;

  // Values
  static values = {
    width: Number,
    height: Number,
    editId: Number,
    parentId: Number, // ID du ContentMedium parent (pour le remplacement dans le publisher)
    layers: Array,
    fonts: Object,
    // Les URLs des catalogues de formes (global + chartes, lib/shape_catalogs.ts) — la lib
    // n'embarque aucune forme, la résolution des clipShape du stock en dépend.
    shapeCatalogs: Array,
    defaultColor: String,
    // Les valeurs du LECTEUR pour les champs liés — jamais écrites dans le document :
    // l'éditeur interpole à l'affichage, le fichier garde ses tokens.
    variables: Object,
    // La médiathèque du lecteur, où entre un fichier lâché sur le canvas.
    uploadEndpoint: { type: String, default: "/toolbox/content_media" },
    // Plan de travail : canvas plein panneau, cadre centré, hors-cadre visible sous un
    // voile (docs/explanation/editor-workspace-viewport.md). Sans : canvas = cadre.
    workspace: Boolean,
    // La page initiale : son id et son numéro (ensuite posés par showSlide)
    pageId: String,
    pageNumber: { type: Number, default: 1 },
    // Le porteur a-t-il un fond (editor_capabilities du porteur) ? Sans, la bande en bord du
    // cadre ne propose rien : tout drop pose un calque (un sticker).
    background: { type: Boolean, default: true },
    // L'infobulle de la pastille d'un calque masqué
    hiddenLayerTooltip: String,
  };

  declare widthValue: number;
  declare heightValue: number;
  declare layersValue: LayerData[];
  declare fontsValue: FontsConfig;
  declare shapeCatalogsValue: string[];
  declare defaultColorValue: string;
  declare hiddenLayerTooltipValue: string;
  declare backgroundValue: boolean;
  declare variablesValue: Record<string, string>;
  declare uploadEndpointValue: string;
  declare workspaceValue: boolean;
  declare pageIdValue: string;
  declare pageNumberValue: number;



  // Instance de l'éditeur
  private editor!: FabricEditor;
  private fabricControls!: FabricControls;
  /**
   * Le canvas porte sa première page : avant, l'éditeur se monte (chargement asynchrone des
   * calques) et l'orchestrateur ne doit rien exporter.
   */
  ready = false;
  /** La page du document que le canvas montre — pour les sous-pages de la toolbox. */
  pageId: string | null = null;
  /** Son numéro, pour son nom (« Slide 2 · 4:5 »). */
  private pageNumber = 1;
  /** Le dernier fond demandé (swapBackground). */
  private backgroundToken: object = {};
  private fabricLayout!: FabricLayout;
  private fabricAppearance!: FabricAppearance;
  private detachBoundTextEditing?: () => void;
  private hiddenLayerBadges?: HiddenLayerBadges;
  private undoManager!: UndoManager;
  private imageDropHandler!: DropHandler;
  private picker!: MediaPicker;
  private unregisterShortcuts: (() => void) | null = null;

  private boundOnToolboxItem!: (e: Event) => void;
  private boundOnToolboxDragStart!: (e: Event) => void;
  private boundOnFileDragOver!: (e: DragEvent) => void;
  private boundOnZoom!: (e: Event) => void;
  private toolboxDrop!: ToolboxDropZone;


  async connect(): Promise<void> {
    this.pageId = this.pageIdValue || null;
    this.pageNumber = this.pageNumberValue;

    // Toolbox — listen for item selections from the left panel
    this.boundOnToolboxItem = this.onToolboxItemSelected.bind(this);
    this.element.addEventListener("toolbox-item:selected", this.boundOnToolboxItem);

    // Zoom — listen for zoom changes from the editor-zoom controller
    this.boundOnZoom = (e: Event) => {
      const zoom = (e as CustomEvent).detail?.zoom;
      if (typeof zoom === "number") this.editor.setUserZoom(zoom);
    };
    this.element.addEventListener("editor-zoom:change", this.boundOnZoom);

    // Toolbox — arm the external drag when a toolbox item starts dragging
    this.boundOnToolboxDragStart = (e: Event) => armToolboxDrag(this.toolboxHost, (e as CustomEvent).detail);
    this.element.addEventListener("toolbox-item:dragstart", this.boundOnToolboxDragStart);

    // Toolbox — drop items onto the canvas (media, text, shapes). La bande des ~50px au
    // bord de l'artboard propose LE FOND (façon Canva) : cue visuel au survol, drop routé
    // vers _applyBackground au lieu de poser un calque.
    this.toolboxDrop = new ToolboxDropZone(this.element, {
      onDragOver: (e: DragEvent) => {
        this.imageDropHandler.trackPointer(e);
        this._toggleEdgeCue(this._nearArtboardEdge(e));
      },
      onDrop: async (e: DragEvent, data: any) => {
        this._toggleEdgeCue(false);
        if (data.url && this._nearArtboardEdge(e)) {
          this.imageDropHandler.cancelDrag();
          // Une carte dynamique déposée en bord : le FOND hérite du binding.
          const bindings = data.dynamic ? { [data.dynamic.field]: { expr: data.dynamic.expr, scope: data.dynamic.scope } } : undefined;
          await this._applyBackground(data.url, bindings, { meta: mediumMeta(data) });
          return;
        }
        await dropToolboxItem(this.toolboxHost, e, data);
      },
      onLeave: () => {
        this._toggleEdgeCue(false);
        this.imageDropHandler.suspendDrag();
      },
      onDragEnd: () => {
        this._toggleEdgeCue(false);
        this.imageDropHandler.cancelDrag();
      },
      ghostZone: this.containerTarget,
    });

    // Media picker — routes media library selections to the right handler
    this.picker = new MediaPicker(document.getElementById("edit_dialog"));
    this.picker.on("fabric:add", async (cm: { id?: number; url?: string }) => {
      await this.fabricControls.addImage(cm);
      this.updateLayersData();
    });
    this.picker.on("fabric:replace", async (cm: { id?: number; url?: string }) => {
      await this.fabricControls.replaceSelectedImage(cm);
    });

    // Créer l'éditeur (canvas at native resolution, CSS handles display scaling)
    this.editor = new FabricEditor(this.canvasTarget, {
      width: this.widthValue,
      height: this.heightValue,
      defaultColor: this.defaultColorValue,
      container: this.containerTarget,
      fonts: this.fontsValue,
      userSlotLabel: this.t("user_slot", "À fournir"),
      userSlotPrompt: this.t("user_slot_prompt", "Uploadez ou glissez une photo dans le cadre"),
      workspace: this.workspaceValue ? workspace(this.element) : undefined,
      // Les variables se lisent en couleur secondaire, sur la slide comme dans le panneau.
      tokenColor: themeColor(this.element, "brand_secondary") ?? undefined,
    });
    if (this.workspaceValue && this.hasFrameTarget) followFrame(this.editor, this.frameTarget);
    if (this.workspaceValue && this.hasFrameCueTarget) followFrame(this.editor, this.frameCueTarget);
    if (this.workspaceValue && this.hasPageOverlayTarget) followFrame(this.editor, this.pageOverlayTarget);
    // Le cadre est placé : la transition du frame peut se jouer (lib/frame_transitions).
    this.previewAreaTarget.dispatchEvent(new CustomEvent("frame:settled", { bubbles: true }));
    await this.editor.replaceAllLayers(this.layersValue);
    this.ready = true;
    if (this.hasHiddenLayersTarget) {
      this.hiddenLayerBadges = new HiddenLayerBadges(this.editor, {
        layer: this.hiddenLayersTarget,
        badge: this.hiddenBadgeTarget,
        variable: this.hiddenVariableTarget,
        panel: this.hiddenPanelTarget,
        tooltip: this.hiddenLayerTooltipValue,
      }, {
        toggle: (layerId, reveal) => this.toggleHiddenLayer(layerId, reveal),
        // Le menu « Variables du document » s'ouvre sur ce champ (variable-overrides#focus)
        editVariable: (key) => window.dispatchEvent(new CustomEvent("variables:focus", { detail: { key } })),
      });
    }
    // Le document porte des tokens ; ce lecteur-ci voit SES valeurs.
    this.interpolate();

    // Undo/redo manager (snapshots the fabric layers JSON)
    this.undoManager = new UndoManager({
      serialize: () => this.editor.persistence.exportLayersJSON(),
      restore: async (snapshot: string) => {
        await this.editor.replaceAllLayers(JSON.parse(snapshot));
        this.interpolate(); // les snapshots portent les tokens, comme le document
        this.layerControlsTarget.hidden = true;
        this.refreshPage();
      },
    });
    this.undoManager.onStateChange = (state) => {
      this.updateUndoRedoButtons(state.canUndo, state.canRedo);
      // Chaque snapshot d'undo = une mutation du canvas : l'orchestrateur (autosave)
      // écoute en bubbling — inoffensif pour les montages fabric sans orchestrateur.
      this.element.dispatchEvent(new CustomEvent("fabric:doc-changed", { bubbles: true }));
    };
    this.undoManager.initialize();

    // Shared layer controls (floating toolbar, reposition, lock icons)
    this.fabricControls = new FabricControls(this.editor, this.layerControlsTarget, {
      onModified: () => this.updateLayersData(),
      onSelect: () => {
        window.dispatchEvent(new CustomEvent("toolbox:show", {
          detail: { key: "overlay" },
        }));
        this.publishPlacement();
      },
      getPageId: () => this.pageId,
      // Sans objet, la sous-page d'un calque revient à la home ; la bibliothèque ou un onglet
      // ouvert par l'utilisateur restent (on vient peut-être d'y prendre un média)
      onSelectMany: () => this.leaveLayerPage(),
      onDeselect: () => this.leaveLayerPage(),
      // La page se sélectionne (clic à vide dans le cadre, ou sur son nom) : la barre de son
      // fond, la page Fond. Le fond est inerte au ciblage — le drop reste libre.
      page: this.hasPageOverlayTarget ? {
        bar: () => this.pageBar(),
        // Sa barre au bas de l'aperçu, contre la bande des slides — comme la séquence en vidéo
        dock: "bottom",
        target: () => (this.pageId ? { kind: "slide" as const, id: this.pageId } : null),
        onSelect: () => this.publishPlacement(),
        onDeselect: () => this.publishPlacement(),
      } : undefined,
      ...this.editor.getControlsConfig(this.previewAreaTarget),
    });
    this.fabricControls.attach();
    this.refreshPage();

    // Layout — shared lib for drag-to-layout + panel integration
    this.fabricLayout = new FabricLayout(this.editor, {
      onChanged: () => this.updateLayersData(),
    });
    this.fabricLayout.attach();

    // Appearance — stroke/fill panel events
    this.fabricAppearance = new FabricAppearance(this.editor, {
      onChanged: () => this.updateLayersData(),
    });
    this.fabricAppearance.attach();

    // Un texte lié s'édite dans sa textbox : à la sortie, export et ré-affichage
    this.detachBoundTextEditing = attachBoundTextEditing(this.editor, () => {
      this.updateLayersData();
      this.interpolate();
    });

    // Reposition controls on container resize
    this.editor.onResize(() => this.fabricControls?.repositionActive());

    // Configurer le drag & drop pour ajout/remplacement d'images. Un fichier du bureau
    // s'affiche tout de suite (url de session) et entre dans la bibliothèque pendant ce
    // temps : le calque porte son CM (autosave) et le flux legacy retrouve la dépendance
    // par l'url. Au bord de l'artboard, il devient LE FOND, comme une carte de la toolbox.
    this.imageDropHandler = new DropHandler(this.editor, {
      hoverDelay: 500,
      getImageUrl: (file) => URL.createObjectURL(file),
      resolveFile: async (file) => {
        const cm = await uploadToLibrary(file, this.uploadEndpointValue);
        return { url: cm.url, imageMeta: mediumMeta(cm) };
      },
      onFile: (file, e) => this._takeFileAsBackground(file, e),
      // Une image du canvas glissée au bord : LE FOND, comme une carte de la toolbox — avec
      // son binding, si elle est dynamique.
      canvasEdge: {
        near: (e) => this._nearArtboardEdge(e),
        cue: (on) => this._toggleEdgeCue(on),
        take: (source) => this._takeCanvasImageAsBackground(source),
      },
      overlayElement: this.hasReplaceOverlayTarget ? this.replaceOverlayTarget : undefined,
      onSuccess: () => this.updateLayersData(),
      onError: (error) => console.error("Erreur lors du traitement de l'image:", error),
    });
    this.imageDropHandler.attach(this.containerTarget);
    // Le cue de la bande du fond pour un drag de fichiers (la ToolboxDropZone ne voit que
    // les cartes) ; le DropHandler de la lib garde le drop.
    this.boundOnFileDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes("Files")) this._toggleEdgeCue(this._nearArtboardEdge(e));
    };
    this.containerTarget.addEventListener("dragover", this.boundOnFileDragOver);

    // Les raccourcis de cet éditeur, dans le gestionnaire de la lib — la table du canvas y est
    // déjà (supprimer, dupliquer, copier-coller, grouper, décaler…) ; l'historique est à l'hôte.
    this.unregisterShortcuts = this.editor.shortcuts.register([
      { id: "undo", keys: ["mod+z"], group: "edit", run: () => this.undo() },
      { id: "redo", keys: ["mod+shift+z", "mod+y"], group: "edit", run: () => this.redo() },
    ]);

    // Les formes arrivent après l'éditeur (lib/shape_catalogs) : un clip legacy affiché en
    // rectangle se remonte quand son catalogue est là.
    streamShapeCatalogs(this.shapeCatalogsValue, () => this.remountLegacyClips());
  }

  disconnect(): void {
    this.ready = false;
    this.element.removeEventListener("toolbox-item:selected", this.boundOnToolboxItem);
    this.element.removeEventListener("toolbox-item:dragstart", this.boundOnToolboxDragStart);
    this.element.removeEventListener("editor-zoom:change", this.boundOnZoom);
    this.fabricLayout?.destroy();
    this.fabricAppearance?.destroy();
    this.detachBoundTextEditing?.();
    this.toolboxDrop?.destroy();
    this.picker?.dispose();
    this.unregisterShortcuts?.();
    this.fabricControls?.destroy();
    this.hiddenLayerBadges?.destroy();
    this.containerTarget?.removeEventListener("dragover", this.boundOnFileDragOver);
    this.imageDropHandler?.detach();
    this.editor?.dispose();
  }

  // === API pour l'éditeur de compositions (bande de slides, via outlet) ===
  // Le canvas n'édite qu'une page à la fois : la bande sérialise la page courante avant de
  // charger la suivante. L'historique undo repart par page — pas d'undo cross-slides.

  exportLayers(): LayerData[] {
    return this._withExpressions(JSON.parse(this.editor.persistence.exportLayersJSON()));
  }

  /** Remonte la page si un clip par id seul vient de devenir résoluble (rare, à l'ouverture). */
  private async remountLegacyClips(): Promise<void> {
    if (!this.ready) return;

    const layers = this.exportLayers();
    if (!hasResolvableLegacyClip(layers)) return;

    // Pendant le remontage, le canvas est en transit : l'orchestrateur n'exporte rien.
    this.ready = false;
    await this.showSlide(layers, this.pageId, this.pageNumber, this._legacyHtml);
    this.ready = this.element.isConnected;
  }

  async showSlide(layers: LayerData[], pageId: string | null = null, pageNumber = 1, legacyHtml = false): Promise<void> {
    // Une autre page : celle d'avant n'est plus sélectionnée
    this.fabricControls?.deselectPage();
    this.pageId = pageId;
    this.pageNumber = pageNumber;
    this._legacyHtml = legacyHtml;
    await this.editor.replaceAllLayers(layers);
    // Le document porte des tokens (les exports les restaurent) : chaque montage de page
    // ré-interpole pour CE lecteur — sans quoi naviguer dans le strip dés-interpolait.
    this.interpolate();
    this.undoManager?.initialize();
    this.refreshPage();
  }

  /** La page que le canvas montre (l'orchestrateur, à la connexion). */
  setPage(pageId: string | null, pageNumber: number, legacyHtml = false): void {
    this.pageId = pageId;
    this.pageNumber = pageNumber;
    this._legacyHtml = legacyHtml;
    if (this.editor) this.refreshPage();
  }

  /** LEGACY_ERB — la page montre un gabarit HTML (famille ERB) : la directive du template
   *  (sans calque `bg`, le HTML servi sous le canvas transparent tient lieu de fond) ou la
   *  provenance du fond nu d'une instance. Posé par l'orchestrateur avec la page. */
  private _legacyHtml = false;

  /** LEGACY_ERB — le fond nu d'une page dont le HTML est servi sous le canvas : son damier
   *  d'attente le cacherait, la lib l'efface (le cadre reste en attente, rien ne change au
   *  save). Rejoué à chaque refreshPage : le premier montage (connect) comme les changements
   *  de page passent par là. */
  private concealPendingBackground(): void {
    const bg = this.editor.layers.findById(BG_LAYER_ID) as ImageFrame | null;
    if (!bg?.pending) return;

    bg.concealPlaceholder();
    this.editor.canvas.originalFabricCanvas.requestRenderAll();
  }

  // ==================== La page et son fond ====================
  // La page se sélectionne (FabricControls) ; son fond est son calque `bg` (lib
  // fabric_background) : une forme couleur, une image, ou rien — transparente.

  /** Le nom de la page : un clic la sélectionne — ou la désélectionne. */
  selectPage(): void {
    this.fabricControls?.togglePage();
  }

  /** La vignette de la page cliquée dans la bande : la page sélectionnée, la barre de son fond. */
  showPageBar(): void {
    this.fabricControls?.selectPage();
  }

  /** L'invitation d'une page vierge : la page, sur l'onglet Couleur de la page Fond. */
  chooseBackgroundColor(): void {
    this.fabricControls?.selectPage("color");
  }

  /** Le color picker de la page Fond : une couleur, un dégradé (une valeur CSS) — transparent :
   *  plus de fond. */
  async setBackgroundColor(event: CustomEvent<{ value?: string }>): Promise<void> {
    const value = event.detail?.value;
    if (!value) return;
    if (value === "transparent") return this.removeBackground();

    await this.replaceBackground(colorBackgroundLayer(this.editor.width, this.editor.height, value));
  }

  /** Plus de fond : la page est transparente. */
  async removeBackground(): Promise<void> {
    await this.replaceBackground(null);
  }

  /** Les outils de la barre de la page, et ce qu'elle montre du fond. */
  private pageBar() {
    const state = backgroundStateOf(this.exportLayers());
    // LEGACY_ERB : sans fond posé, le gabarit HTML est le fond — pas d'outil, la mention.
    if (this._legacyHtml && state.kind === "none") {
      return { tools: ["backgroundLegacy"], state: { color: "transparent", backgroundKind: "html" } };
    }
    const tools = ["backgroundColor", "backgroundImage"];
    if (state.kind === "image") tools.push("backgroundCrop");
    if (state.kind !== "none") tools.push("backgroundRemove");
    return { tools, state: { color: state.css || "transparent", backgroundKind: state.kind } };
  }

  /** Le nom de la page et son invitation (vierge : rien que son fond blanc d'origine, ou
   *  rien) suivent le canvas. */
  private refreshPage(): void {
    if (this.hasPageNameTarget) {
      this.pageNameTarget.textContent = pageName(this.pageNameTarget.dataset.nameTemplate || "%{number}",
        this.pageNumber, this.editor.width, this.editor.height);
    }
    // LEGACY_ERB : une page à gabarit HTML n'est pas vierge, le dit, et laisse voir le HTML.
    if (this.hasPageEmptyTarget) this.pageEmptyTarget.hidden = this._legacyHtml || !isBlankLayers(this.exportLayers());
    if (this.hasLegacyHintTarget) this.legacyHintTarget.hidden = !this._legacyHtml;
    if (this._legacyHtml) this.concealPendingBackground();
    this.fabricControls?.refreshPage();
    this.publishPlacement();
  }

  /** Pose (ou retire, `null`) LE fond de la page, et enregistre. */
  private async replaceBackground(bg: LayerData | null, dropLayerId?: string): Promise<void> {
    if ((await this.swapBackground(bg, dropLayerId)) !== undefined) this.updateLayersData();
  }

  /**
   * Change le seul calque `bg` (sous tous les autres) — et retire le calque promu en fond.
   * Le reste du canvas ne bouge pas : recharger tous les calques (replaceAllLayers) les
   * resélectionnerait un à un, et la page sélectionnée se perdrait à chaque couleur choisie.
   * Rend le nouveau fond (null : plus de fond), undefined si un fond plus récent l'a supplanté.
   */
  private async swapBackground(bg: LayerData | null, dropLayerId?: string): Promise<FabricObject | null | undefined> {
    // Des couleurs choisies en rafale (color picker) : la dernière gagne
    const token = (this.backgroundToken = {});
    const [next] = bg ? await this.editor.layers.deserializeAll([bg]) : [];
    if (token !== this.backgroundToken) return undefined;
    const canvas = this.editor.canvas.originalFabricCanvas;
    const old = this.editor.layers.findById(BG_LAYER_ID);
    const dropped = dropLayerId ? this.editor.layers.findById(dropLayerId) : null;
    if (dropped) {
      canvas.discardActiveObject();
      canvas.remove(dropped);
    }
    if (old) canvas.remove(old);
    if (next) canvas.insertAt(0, next);
    canvas.requestRenderAll();
    return next || null;
  }

  // === Actions Stimulus ===

  addText(): void {
    this.fabricControls.addText({
      text: this.t("default_text", "Tapez votre texte ici"),
    });
    this.updateLayersData();
  }

  // Récupère une traduction du namespace js.fabric exposé via window.jsTranslations
  private t(key: string, fallback: string): string {
    return (window as any).jsTranslations?.fabric?.[key] ?? fallback;
  }


  addShape(): void {
    this.fabricControls.addShape({
      fill: this.defaultColorValue || undefined,
    });
    this.updateLayersData();
  }

  pickReplaceImage(): void {
    this.picker.open("fabric:replace");
  }

  // ==================== Actions — events from overlay panel ====================

  deselectCanvas(): void {
    this.editor?.selection?.clear();
  }

  deselectOutside(e: Event): void {
    const wrapper = this.containerTarget.querySelector("[data-fabric='wrapper']");
    if (wrapper?.contains(e.target as Node)) return;
    // TODO: fragile — relies on specific data-attributes to exclude toolbar UI from
    // triggering deselection. Consider a single wrapper element or a CSS class instead.
    if ((e.target as Element).closest("[data-role='layerControls'], [data-role='cropBar'], [data-floating-menu-target='menu']")) return;
    this.editor?.selection?.clear();
  }

  setCornerRadius(e: Event): void {
    const radius = (e as CustomEvent).detail?.value;
    if (typeof radius !== "number") return;
    const obj = this.editor?.selection?.current as any;
    if (obj?.setCornerRadius) {
      obj.setCornerRadius(radius);
      this.updateLayersData();
    }
  }

  // ==================== Layer control actions (delegated to FabricControls) ====================

  fcDelete(): void { this.fabricControls?.deleteSelection(); }
  fcToggleOutline(): void { this.fabricControls?.toggleOutline(); }
  fcSetLockMode({ params }: { params: { mode: LockMode } }): void { this.fabricControls?.setLockMode(params.mode); }
  fcContainerize(): void { this.fabricControls?.containerize(); }
  /** Garder la sélection en calque : l'orchestrateur, qui tient l'autosave et le porteur,
   *  flush puis poste les ids (sticker:extract) — le serveur lit le document sauvegardé. */
  fcSaveAsSticker(): void {
    const layerIds = (this.editor?.selection.selected ?? [])
      .map((obj) => obj.get("layerId") as string | undefined)
      .filter((id): id is string => Boolean(id));
    if (!layerIds.length) return;
    window.dispatchEvent(new CustomEvent("sticker:extract", { detail: { layerIds, pageId: this.pageIdValue } }));
  }
  fcCrop(): void { this.fabricControls?.cropSelection(); }
  fcCropBackground(): void { this.fabricControls?.cropBackground(); }
  fcCropReset(): void { this.fabricControls?.cropReset(); }
  fcCropCancel(): void { this.fabricControls?.cropCancel(); }
  fcCropDone(): void { this.fabricControls?.cropDone(); }
  fcBringForward(): void { this.fabricControls?.bringForward(); }
  fcSendBackward(): void { this.fabricControls?.sendBackward(); }
  fcGroup(): void { this.fabricControls?.group(); }
  fcUngroup(): void { this.fabricControls?.ungroup(); }
  fcSetArrangement({ params }: { params: { arrangement: Arrangement } }): void {
    this.fabricControls?.setArrangement(params.arrangement);
  }
  fcAlign({ params }: { params: { edge: AlignEdge } }): void { this.fabricControls?.align(params.edge); }
  fcDistribute({ params }: { params: { axis: DistributeAxis } }): void { this.fabricControls?.distribute(params.axis); }




  private onToolboxItemSelected(e: Event): void {
    const item = (e as CustomEvent).detail;
    if (item?.type !== "media") {
      void placeToolboxItem(this.toolboxHost, item);
      return;
    }
    // Cet éditeur ne pose aucune vidéo : elle n'a pas de cible.
    const current = mediaKind(item) === "image" ? this.imagePlacement() : null;
    void placeToolboxItem(this.toolboxHost, item, "placement" in item ? item.placement : current);
  }

  /** Où irait une image : le fond de la page sélectionnée (comme une forme sélectionnée
   *  prendrait l'image pour la sienne), sinon la cible du canvas. */
  private imagePlacement(): Placement | null {
    if (!this.editor) return null;
    if (this.fabricControls?.isPageSelected && this.pageId) {
      return { action: "background", page: this.pageId };
    }
    return canvasPlacement(this.editor, this.pageId);
  }

  /** La cible courante, pour l'en-tête de la toolbox et le gel des uploads. */
  /** Plus d'objet seul sélectionné : la sous-page d'un calque revient à la home. */
  private leaveLayerPage(): void {
    if (this.fabricControls?.layerPageOpen()) {
      window.dispatchEvent(new CustomEvent("toolbox:show", { detail: { key: "home" } }));
    }
    this.publishPlacement();
  }

  private publishPlacement(): void {
    publishPlacements({ image: this.imagePlacement(), video: null });
  }

  /** Cet éditeur, vu par la toolbox (lib toolbox_dispatch, partagée avec le compositeur
   *  vidéo) : ses défauts, et son rituel — l'export des layers. */
  private get toolboxHost(): ToolboxHost {
    return {
      editor: this.editor,
      controls: this.fabricControls,
      dropHandler: this.imageDropHandler,
      defaults: { text: this.t("default_text", "Tapez votre texte ici"), fill: this.defaultColorValue || undefined },
      onChanged: () => this.updateLayersData(),
      currentPage: () => this.pageId,
      // Le fond de la page : seulement s'il s'agit encore de la page affichée.
      placeElsewhere: (item, placement) => {
        if (placement.action !== "background" || placement.page !== this.pageId || !item.url) return false;
        void this._applyBackground(item.url, undefined, { meta: mediumMeta(item) });
        return true;
      },
    };
  }

  // === Méthodes privées ===

  // Selection callbacks and layer controls are now handled by FabricControls

  private updateLayersData(): void {
    this.refreshPage();
    // Enregistrer dans l'historique pour undo/redo
    this.undoManager.push();
  }

  /** Le document vient d'être sauvegardé : la sous-page ouverte de la toolbox le suit. */
  reloadOverlayPanel(): void {
    this.fabricControls?.reloadOverlayPanel();
  }

  private updateUndoRedoButtons(canUndo: boolean, canRedo: boolean): void {
    if (this.hasUndoButtonTarget) {
      this.undoButtonTarget.disabled = !canUndo;
    }
    if (this.hasRedoButtonTarget) {
      this.redoButtonTarget.disabled = !canRedo;
    }
  }

  /** En recadrage, ⌘Z annule le recadrage — rien d'autre (il n'est pas encore enregistré). */
  async undo(): Promise<void> {
    if (this.editor.crop.active) return this.editor.crop.cancel();
    await this.undoManager.undo();
  }

  async redo(): Promise<void> {
    await this.undoManager.redo();
  }


  // ==================== Bindings (dialecte template) ====================
  // Le calque porte ses bindings (le package les préserve et les signale) ; le panneau
  // Variables, lui, vit côté app — il écoute la sélection et pilote ces deux verbes.

  /** Le format se décide en cours d'édition — délégation vers l'éditeur (calques en place,
   *  sauf le fond, structurel : il se recale plein cadre). */
  /** Attendable : le fond se recale de façon asynchrone, et l'appelant sauve derrière. */
  async resizeArtboard(width: number, height: number): Promise<void> {
    this.editor.resizeArtboard(width, height);
    await this._refitBackground();
    this.refreshPage();
  }

  /** Promouvoir l'image sélectionnée en FOND : elle quitte le canvas et réapparaît en
   *  ImageFrame plein cadre (cover), inerte au targeting (le drag & drop reste libre — un
   *  fond qui réagit au drop interdirait de dropper quoi que ce soit) ; l'ancien fond meurt.
   *  Ses bindings suivent : promouvoir un slot média donne un fond dynamique. */
  async promoteToBackground(): Promise<void> {
    const selected = this.editor?.selection?.current as any;
    if (!selected || !rulesOf(selected).options.includes("image")) return;

    const layerId = selected.get("layerId");
    const layers = this.exportLayers() as any[];
    const source = layers.find((layer) => layer.layerId === layerId);
    if (!source?.image?.src) return;

    await this._applyBackground(source.image.src, source.bindings,
      { dropLayerId: layerId, meta: imageMetaOf(source.image) });
  }

  /** La bande de promotion du fond : dans le cadre mais à moins de ~50px d'un bord — toute
   *  la page quand elle est vierge (l'invitation le dit). */
  /** La bande du fond (lib editor_stage) ; une page vide prend tout le cadre. */
  private _nearArtboardEdge(e: { clientX: number; clientY: number }): boolean {
    if (!this.backgroundValue) return false;
    const whole = this.hasPageEmptyTarget && !this.pageEmptyTarget.hidden;
    return nearFrameEdge(this.editor, this.containerTarget, e, { whole });
  }

  private _toggleEdgeCue(on: boolean): void {
    // Plan de travail : le repère dédié, au-dessus du canvas (sous lui, la couche `frame`
    // serait couverte par le fond du plan de travail). Sinon, l'artboard qui l'enveloppe.
    if (this.hasFrameCueTarget) {
      this.frameCueTarget.classList.toggle("hidden", !on);
      return;
    }
    this.containerTarget.parentElement?.classList.toggle("ring-4", on);
    this.containerTarget.parentElement?.classList.toggle("ring-fuchsia-400", on);
  }

  /** Pose (ou remplace) LE fond : plein cadre cover, lockMode position — jamais déplaçable,
   *  mais repositionnable (crop) et remplaçable une fois sélectionné (clic à vide). Inerte
   *  au targeting fabric : la sélection passe par le clic-à-vide, jamais par le drop. */
  /** Un fichier du bureau lâché dans la bande du fond : il devient le fond tout de suite (url
   *  de session, source en attente — jamais sauvée), et l'upload le remplace en place. */
  private _takeFileAsBackground(file: File, e: DragEvent): boolean {
    if (!this._nearArtboardEdge(e)) return false;

    this._toggleEdgeCue(false);
    void this._applyBackground(URL.createObjectURL(file), undefined, { sourcePending: true }).then(() => {
      const bg = this.editor.layers.findById("bg");
      if (bg) return this.imageDropHandler.resolveFileInto(bg, file);
    });
    return true;
  }

  /** Une image du canvas lâchée dans la bande du fond : son image devient le fond (le calque
   *  part, la lib le retire) ; une image dynamique y porte son binding. */
  private async _takeCanvasImageAsBackground(source: FabricObject): Promise<boolean> {
    const image = canvasImageSource(source);
    if (!image) return false;

    const binding = (source.get("bindings") as Record<string, unknown> | undefined)?.["image.src"];
    await this._applyBackground(image.url, binding ? { "image.src": binding } : undefined, { meta: image.imageMeta });
    return true;
  }

  private async _applyBackground(
    src: string,
    bindings?: object,
    { dropLayerId, meta = {}, sourcePending = false }: { dropLayerId?: string; meta?: ImageMeta; sourcePending?: boolean } = {},
  ): Promise<void> {
    const bg = imageBackgroundLayer(this.editor.width, this.editor.height, src, { meta, bindings });
    const frame = await this.swapBackground(bg, dropLayerId);
    if (frame === undefined) return;
    // Marqué AVANT l'export : une url de session n'entre jamais dans le document.
    if (sourcePending) (frame as ImageFrame | null)?.markSourcePending();
    this.updateLayersData();
  }

  /** Le fond suit l'artboard : au changement de format, il se recale plein cadre — il est
   *  structurel, contrairement aux calques qui restent en place. */
  private async _refitBackground(): Promise<void> {
    const layers = this.exportLayers();
    const bg = layers.find((layer) => (layer as { layerId?: string }).layerId === BG_LAYER_ID);
    if (!bg) return;

    await this.replaceBackground(refitBackgroundLayer(bg as never, this.editor.width, this.editor.height));
  }

  /** Lie un champ du calque sélectionné (lib fabric_bindings, partagée avec le compositeur
   *  vidéo) — puis le rituel de CET éditeur : export des layers, contexte, interpolation
   *  d'affichage avec les valeurs du lecteur courant. */
  bindField(event: CustomEvent): void {
    if (!bindSelectedField(this.editor, event.detail)) return;

    this.updateLayersData();
    this.interpolate();
  }

  /** Le panneau Variables insère une variable : si un texte est en édition sur la slide, elle
   *  va à SON curseur (lib fabric_bindings) et le panneau relit l'expression dans l'événement ;
   *  sinon rien, le panneau insère chez lui. */
  insertToken(event: CustomEvent): void {
    const expr = insertTokenAtCaret(this.editor, event.detail);
    if (expr === null) return;

    event.detail.expr = expr;
    this.updateLayersData();
  }

  /** Pose le texte du calque sélectionné (sous-page Variables : une expression sans token
   *  n'est pas une liaison, c'est du texte). */
  setText(event: CustomEvent): void {
    if (!setSelectedText(this.editor, event.detail?.text)) return;

    this.updateLayersData();
    this.interpolate();
  }

  /** Délie un champ : l'expression disparaît, la valeur en place redevient éditable (et
   *  une condition retirée lève son fantôme : l'affichage se rejoue). */
  unbindField(event: CustomEvent): void {
    if (!unbindSelectedField(this.editor, event.detail?.field || "text")) return;

    this.updateLayersData();
    this.interpolate();
  }

  /** Les valeurs du lecteur changent (menu « Variables du document » : on teste) : le canvas
   *  les rejoue — textes liés, images, conditions. Le document, lui, ne bouge pas. */
  setVariables(event: CustomEvent): void {
    this.variablesValue = event.detail?.values || {};
    this.interpolate();
  }

  /** Rejoué après chaque liaison et à chaque changement de page — le canvas montre ce que
   *  CE lecteur verra, le document garde ses tokens (lib fabric_bindings). La barre suit :
   *  l'œil de l'outil Condition lit le fantôme du calque sélectionné. */
  interpolate(): void {
    interpolateBoundFields(this.editor, this.variablesValue || {});
    this.hiddenLayerBadges?.refresh();
    this.fabricControls?.refreshSelection();
  }

  /** La pastille d'un calque masqué : le révéler en fantôme (réglable, sélectionné) ou le
   *  rééclipser. Rien n'est écrit dans le document. */
  toggleHiddenLayer(layerId: string, reveal: boolean): void {
    revealHiddenLayer(this.editor, layerId, reveal);
    this.interpolate();
    if (!reveal) return;

    const obj = this.editor.layers.all.find((o: any) => o.get("layerId") === layerId);
    if (obj) {
      this.editor.canvas.setActiveObject(obj);
      this.editor.canvas.requestRenderAll();
    }
  }

  /** Ce qu'on EXPORTE est le document, pas son rendu chez ce lecteur (lib fabric_bindings). */
  private _withExpressions(layers: LayerData[]): LayerData[] {
    return withExpressions(layers);
  }

}

// Déclaration pour TypeScript
declare global {
  interface Window {
    openDialog?: (id: string) => void;
    closeDialog?: (id: string) => void;
    closeSidePanel?: (id: string) => void;
  }
}
